import React, {
	useState,
	useMemo,
	memo,
	useCallback,
	useRef,
	useEffect,
} from "react";
import { Stack, Box, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { FullPageContainer } from "../../components/layout/container/FullPageContainer";
import colorsCustom from "../../designTokens/colorsCustom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useResponsive } from "../../hooks/useResponsive";
import TypingEffect from "../../components/patterns/typoraphy/TypingEffect";
import FadeInTypography from "../../components/patterns/typoraphy/FadeInTypography";
import { PageContainer } from "../../components/Container/PageContainer";
import ParallaxContainer from "../../components/scroll/ParallaxContainer";
import ParallaxLayer from "../../components/scroll/ParallaxLayer";
import OptimizedAvatar from "../../components/Avatar/OptimizedAvatar";
import MagazineIssueHeader from "../magazine/MagazineIssueHeader";

// Firebase Storage URL for video streaming - ScrubbingVideoWithFullPage.jsx와 동일한 URL 사용
const WRITING_MAN_VIDEO_URL =
	"https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fwriting_man_landing_low.mp4?alt=media";
// Placeholder image while video loads (optional - can be added later)
const VIDEO_POSTER_URL = null; // 포스터 이미지는 나중에 추가

// 아바타 컴포넌트를 별도로 분리하고 memo로 최적화
const UserAvatar = memo(({ user, isMobile }) => {
	const avatarSize = isMobile ? 35 * user.scale : 80 * user.scale;
	const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);

	// float 애니메이션 값을 useMemo로 고정
	const floatOffset = useMemo(() => {
		// user.id를 기반으로 일관된 랜덤값 생성 (pseudo-random)
		const hash = user.id
			.split("")
			.reduce((acc, char) => acc + char.charCodeAt(0), 0);
		return (hash % 20) - 10; // -10px ~ +10px
	}, [user.id]);

	const handleAvatarClick = useCallback(() => {
		if (user.username) {
			window.open(`https://www.threads.net/@${user.username}`, "_blank");
		}
	}, [user.username]);

	return (
		<>
			{/* 고유한 키프레임 애니메이션 스타일 추가 */}
			<style>
				{`
          @keyframes float-${user.id} {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(${floatOffset}px); }
          }
        `}
			</style>
			<div
				onClick={handleAvatarClick}
				style={{
					opacity: isAvatarLoaded ? 1 : 0,
					transform: isAvatarLoaded ? "translateY(0)" : "translateY(20px)",
					transition: `all ${
						0.8 + user.fadeDelay
					}s cubic-bezier(0.4, 0, 0.2, 1)`,
					transitionDelay: `${user.fadeDelay}s`,
					animation: isAvatarLoaded
						? `float-${user.id} ${user.floatDuration}s ease-in-out infinite`
						: "none",
					animationDelay: `${user.floatDelay}s`,
					cursor: "pointer",
					position: "relative",
					padding: "4px",
					border: "2px solid #1A252F",
					borderRadius: "50%",
					backgroundColor: "white",
				}}
			>
				<OptimizedAvatar
					userID={user.id}
					username={user.username}
					size={avatarSize}
					onLoadChange={setIsAvatarLoaded}
				/>
			</div>
		</>
	);
});

UserAvatar.displayName = "UserAvatar";

export const AllCollectionHeroSection = ({
	titleKey,
	descriptionKey,
	bg,
	c,
	style = {},
	collections = [],
}) => {
	const { t } = useTranslation();
	const isMobile = useIsMobile("md");
	const { smDown, mdDown } = useResponsive();
	const theme = useTheme();
	const [videoLoaded, setVideoLoaded] = useState(false);
	const [videoError, setVideoError] = useState(false);
	const videoRef = useRef(null);

	useEffect(() => {
		// 수동 play() 호출 제거: autoplay + muted + playsInline 속성에 맡긴다
		const v = videoRef.current;
		if (!v) return;
		try {
			v.muted = true;
		} catch (_) {}
	}, []);

	// 컬렉션에서 고유한 사용자 추출 (중복 제거)
	const uniqueUsers = useMemo(() => {
		const userMap = new Map();

		collections.forEach((collection) => {
			if (collection.userID && collection.username) {
				if (!userMap.has(collection.userID)) {
					userMap.set(collection.userID, {
						id: collection.userID,
						username: collection.username,
						name: collection.name,
						profileImage: collection.profileImage,
					});
				}
			}
		});

		return Array.from(userMap.values()).slice(0, 10); // 최대 10명만 표시
	}, [collections]);

	// 아바타 애니메이션 초기 데이터를 한 번만 생성
	const animatedAvatars = useMemo(() => {
		console.log("Collections:", collections.length);
		console.log("UniqueUsers:", uniqueUsers.length);

		// 실제 사용자 데이터가 있으면 사용, 없으면 테스트 데이터 사용
		const users =
			uniqueUsers.length > 0
				? [...uniqueUsers].sort(() => Math.random() - 0.5).slice(0, 10)
				: Array.from({ length: 10 }, (_, index) => ({
						id: `test-${index}`,
						username: `User${index}`,
						name: `Test User ${index}`,
						profileImage: null,
				  }));

		// user.id를 기반으로 일관된 pseudo-random 값 생성
		return users.map((user) => {
			// user.id를 해시값으로 변환하여 일관된 랜덤값 생성
			const hash = user.id
				.split("")
				.reduce((acc, char) => acc + char.charCodeAt(0), 0);
			const seed1 = (hash * 9301 + 49297) % 233280;
			const seed2 = (hash * 5701 + 24593) % 233280;
			const seed3 = (hash * 3301 + 14297) % 233280;

			const pseudoRandom1 = seed1 / 233280; // 0-1 사이 값
			const pseudoRandom2 = seed2 / 233280;
			const pseudoRandom3 = seed3 / 233280;

			const depthZ = isMobile
				? Math.floor(pseudoRandom1 * 30) + 10 // mobile: 10-40
				: Math.floor(pseudoRandom1 * 600) + 200; // desktop: 200-800
			const scale = isMobile
				? 1.2 - ((depthZ - 10) / 30) * 0.6
				: 1.2 - ((depthZ - 200) / 600) * 0.6;

			return {
				...user,
				depthZ,
				scale,
				isVisible: true,
				isAboveText: pseudoRandom2 < 0.3,
				randomOffsetX: isMobile
					? (pseudoRandom3 - 0.5) * 100
					: (pseudoRandom3 - 0.5) * 400,
				fadeDelay: pseudoRandom1 * 2,
				floatDelay: pseudoRandom2 * 3,
				floatDuration: 3 + pseudoRandom3 * 2,
			};
		});
	}, [uniqueUsers, collections.length, isMobile]); // collections 대신 collections.length 사용

	// 이제 UserAvatar 컴포넌트는 상단에 정의되어 있으므로 제거

	return (
		<Stack spacing={0} width="100%">
			{/* 히어로 섹션에만 패럴럭스 적용 */}
			<FullPageContainer bg={bg} c={c} style={style}>
				<ParallaxContainer
					maxZ={isMobile ? 50 : 1000}
					style={{ minHeight: "100vh" }}
				>
					<PageContainer
						expand={true}
						sx={{
							position: "relative",
							padding: 0, // 패딩 제거로 전체 화면 사용
							margin: "0 auto",
							px: mdDown ? 2 : 0,
						}}
					>
						<MagazineIssueHeader isMobile={isMobile} />
						{/* 타이틀과 설명 - 가장 느린 패럴럭스 */}
						<ParallaxLayer
							depthZ={isMobile ? 45 : 950}
							style={{
								padding: 0,
								position: "relative",
								alignItems: "flex-start",
								minHeight: "100vh",
								paddingTop: isMobile ? "0vh" : "0vh",
								pointerEvents: "none",
							}}
						>
							<Stack
								spacing={3}
								sx={{
									position: "relative",
									pointerEvents: "none",
									px: isMobile ? 2 : 0, // 텍스트 영역에 패딩 복원
									maxWidth: "100%",
									boxSizing: "border-box",
								}}
							>
								<FadeInTypography
									text={t(titleKey || "magazine.monthlyThreadsTitle")}
									variant={isMobile ? "h2" : "h1"}
									direction="up"
									speed={600}
									wordDelay={240}
									sx={{
										fontFamily: theme.other?.displayHeadings?.fontFamily,
										fontWeight: theme.other?.displayHeadings?.[isMobile ? 'd3' : 'd1']?.fontWeight,
										fontSize: theme.other?.displayHeadings?.[isMobile ? 'd3' : 'd1']?.fontSize,
										lineHeight: theme.other?.displayHeadings?.[isMobile ? 'd3' : 'd1']?.lineHeight,
										letterSpacing: theme.other?.displayHeadings?.[isMobile ? 'd3' : 'd1']?.letterSpacing,
										wordSpacing: theme.other?.displayHeadings?.[isMobile ? 'd3' : 'd1']?.wordSpacing,
										color: theme.palette.text.primary,
										position: "relative",
										zIndex: 10,
										pointerEvents: "auto",
									}}
								/>
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: isMobile ? "flex-start" : "center",
										position: "relative",
										width: isMobile ? "100%" : "fit-content",
										maxWidth: "100%",
										zIndex: 10,
										pointerEvents: "auto",
									}}
								>
									<TypingEffect
										texts={[t(descriptionKey)]}
										typingSpeed={80}
										startDelay={800}
										cursorType="circle"
										fontFamily='"source-han-serif-korean", "Noto Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", serif'
										fontWeight={600}
										textAlign={isMobile ? "left" : "center"}
										cursorColor={theme.palette.text.primary}
										textColor={colorsCustom.ui.dark.bold}
										variant={isMobile ? "h5" : "h4"}
									/>
								</Box>
							</Stack>
						</ParallaxLayer>

						{/* 비디오 - 중간 속도 패럴럭스 */}
						<ParallaxLayer
							depthZ={isMobile ? 40 : 600}
							style={{
								padding: 0,
								position: "absolute",
								top: "-240px",
								left: 0,
								width: "100%",
								height: "100%",
								pointerEvents: "none",
								zIndex: 1,
							}}
						>
							<Box
								sx={{
									position: "absolute",
									bottom: 0,
									right: 0,
									width: smDown ? "70%" : "31.5vw",
									height: isMobile ? "auto" : "auto",
									overflow: "hidden",
									maxWidth: "100%",
								}}
							>
								{!videoError && (
									<video
										ref={videoRef}
										autoPlay
										loop
										muted
										playsInline
										preload="metadata"
										poster={VIDEO_POSTER_URL || undefined}
										onLoadedData={() => setVideoLoaded(true)}
										onError={() => {
											console.error("Video failed to load");
											setVideoError(true);
										}}
										style={{
											width: "100%",
											height: "auto",
											maxWidth: "100%",
											objectFit: "cover",
											opacity: videoLoaded ? 1 : 0,
											transition: "opacity 0.5s ease-in-out",
										}}
										// 캐싱 개선을 위한 속성
										crossOrigin="anonymous"
									>
										<source
											src={`${WRITING_MAN_VIDEO_URL}&cache-control=public,max-age=31536000`}
											type="video/mp4"
										/>
										{/* Fallback for browsers that don't support video */}
										Your browser does not support the video tag.
									</video>
								)}
								{/* Fallback image if video fails to load */}
								{videoError && (
									<img
										src={VIDEO_POSTER_URL}
										alt="Writing man"
										style={{
											width: "100%",
											height: "auto",
											maxWidth: "100%",
											objectFit: "cover",
										}}
									/>
								)}
							</Box>
						</ParallaxLayer>
					</PageContainer>
				</ParallaxContainer>
			</FullPageContainer>

			{/* 기존 아바타 패럴럭스 영역 - 개별 패럴럭스 유지 */}
			<Box
				sx={{
					position: "relative",
					width: "100%",
					height: isMobile ? "80vh" : "100vh", // 모바일에서는 높이 감소
					marginTop: isMobile ? "-80vh" : "-80vh", // 모바일에서는 간격 조정
					pointerEvents: "none",
				}}
			>
				<ParallaxContainer maxZ={isMobile ? 50 : 1000}>
					<Grid
						container
						spacing={isMobile ? 2 : 3}
						sx={{
							width: "100%",
							margin: "0 auto",
							padding: isMobile ? "1rem" : "2rem",
							minHeight: "100vh",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						{animatedAvatars.map((user, index) => (
							<Grid
								key={user.id}
								size={isMobile ? 4 : 3}
								sx={{
									...(index % 2 === 1 && { paddingTop: "1.5rem !important" }), // 홀수 인덱스에 상단 패딩 감소
								}}
							>
								<ParallaxLayer
									depthZ={user.depthZ}
									style={{
										padding: 0, // 패럴럭스 기본 패딩 제거
										position: "relative",
										zIndex: user.isAboveText ? 15 : 5,
										height: isMobile ? "60px" : "80px", // 아바타 컨테이너 높이 감소
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<div
										style={{
											position: "absolute",
											left: "50%",
											top: "50%",
											transform: `translate(-50%, -50%) translateX(${user.randomOffsetX}px)`,
											zIndex: user.isAboveText ? 15 : 5,
											pointerEvents: "auto", // 아바타 클릭 가능하게 변경
										}}
									>
										<UserAvatar user={user} isMobile={isMobile} />
									</div>
								</ParallaxLayer>
							</Grid>
						))}
					</Grid>
				</ParallaxContainer>
			</Box>
		</Stack>
	);
};
